import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  MoonIcon,
  SunIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  DocumentDuplicateIcon,
  SwatchIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../contexts/AuthContext'

interface SettingsItem {
  id: string
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  action: () => void
  badge?: string
  color?: string
}

const SettingsDropdown = () => {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('dark')
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    try {
      await logout()
      navigate('/auth')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const settingsItems: SettingsItem[] = [
    {
      id: 'projects',
      label: 'Active Projects',
      description: 'View and manage your current projects',
      icon: DocumentDuplicateIcon,
      action: () => {
        setIsOpen(false)
        navigate('/projects')
      },
      color: 'text-indigo-400'
    },
    {
      id: 'branding',
      label: 'Branding & Documents',
      description: 'Customize your business branding and document templates',
      icon: SwatchIcon,
      action: () => {
        setIsOpen(false)
        navigate('/branding')
      },
      color: 'text-pink-400'
    },
    {
      id: 'project-templates',
      label: 'Project Templates',
      description: 'Create and manage reusable project workflows',
      icon: DocumentDuplicateIcon,
      action: () => {
        setIsOpen(false)
        navigate('/project-templates')
      },
      color: 'text-indigo-400'
    },
    {
      id: 'appearance',
      label: 'Appearance',
      description: 'Customize theme and display preferences',
      icon: PaintBrushIcon,
      action: () => {
        // Toggle theme for demo purposes
        const newTheme = theme === 'dark' ? 'light' : 'dark'
        setTheme(newTheme)
        // In a real app, you'd save this to localStorage or user preferences
      },
      color: 'text-purple-400'
    },
    {
      id: 'language',
      label: 'Language & Region',
      description: 'Change language and regional settings',
      icon: GlobeAltIcon,
      action: () => {
        setIsOpen(false)
        navigate('/profile?tab=preferences')
      },
      color: 'text-blue-400'
    },
    {
      id: 'shortcuts',
      label: 'Keyboard Shortcuts',
      description: 'View and customize keyboard shortcuts',
      icon: ComputerDesktopIcon,
      action: () => {
        setIsOpen(false)
        // Show keyboard shortcuts modal
        alert('Keyboard shortcuts:\n\nCtrl+N - New Shoot\nCtrl+I - New Invoice\nCtrl+C - New Client\nCtrl+/ - Search')
      },
      color: 'text-emerald-400'
    },
    {
      id: 'help',
      label: 'Help & Support',
      description: 'Get help, contact support, or view documentation',
      icon: QuestionMarkCircleIcon,
      action: () => {
        setIsOpen(false)
        // Open help center in new tab
        window.open('https://help.shootsuite.com', '_blank')
      },
      color: 'text-orange-400'
    }
  ]

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <SunIcon className="w-4 h-4" />
      case 'dark':
        return <MoonIcon className="w-4 h-4" />
      default:
        return <ComputerDesktopIcon className="w-4 h-4" />
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-400 hover:text-white transition-colors touch-manipulation"
      >
        <Cog6ToothIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-72 sm:w-80 rounded-2xl shadow-xl border border-white/30 z-50 max-w-[calc(100vw-2rem)] mx-2 sm:mx-0"
            style={{
              background: 'rgba(0, 0, 0, 0.95)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(255, 255, 255, 0.2)'
            }}
          >
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                  <Cog6ToothIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-white">Settings</h3>
                  <p className="text-xs sm:text-sm text-gray-400">Manage your preferences</p>
                </div>
              </div>
            </div>

            {/* Quick Theme Toggle */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  {getThemeIcon()}
                  <span className="text-white text-sm font-medium">Theme</span>
                </div>
                <div className="flex items-center space-x-1 bg-white/10 rounded-lg p-1">
                  <button
                    onClick={() => setTheme('light')}
                    className={`p-1.5 rounded transition-colors touch-manipulation ${
                      theme === 'light' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <SunIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setTheme('dark')}
                    className={`p-1.5 rounded transition-colors touch-manipulation ${
                      theme === 'dark' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <MoonIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setTheme('system')}
                    className={`p-1.5 rounded transition-colors touch-manipulation ${
                      theme === 'system' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <ComputerDesktopIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Settings Items */}
            <div className="p-2">
              {settingsItems.map((item, index) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={item.action}
                  className="w-full p-3 rounded-xl hover:bg-white/10 transition-all text-left group touch-manipulation"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`flex-shrink-0 ${item.color}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-medium text-sm">{item.label}</p>
                        {item.badge && (
                          <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full flex-shrink-0">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs mt-1 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Footer - Sign Out */}
            <div className="p-4 border-t border-white/10">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSignOut}
                className="w-full flex items-center justify-center space-x-2 p-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-all border border-red-500/20 touch-manipulation"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span className="font-medium text-sm">Sign Out</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SettingsDropdown 